// Argus — Zustand store pentru floating windows MDI (Faza 3).
// State: openWindows Map<windowKey, WindowState> + windowStack ordonat z-order ascendent.
// windowKey = "${agentId}:${panelType}" — unic per (user, agent, panel).
// Persist DB via lib/windowManager helpers (debounced 500ms la drag/resize end).

import { create } from 'zustand';
import type { PanelType } from '../lib/layoutPrefs';

export interface WindowState {
  readonly key: string;
  readonly agentId: string;
  readonly panelType: PanelType;
  // x/y/width/height = MEREU rect-ul natural (drag-able, non-maximized).
  // Maximize NU îl modifică — render-ul `FloatingWindow` calculează fill-canvas separat.
  // Restore din maximized revine direct la x/y/width/height.
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly zOrder: number;
  readonly isMaximized: boolean;
  readonly isMinimized: boolean;
}

interface WindowStoreState {
  openWindows: Map<string, WindowState>;
  windowStack: string[];
  topZ: number;
  upsertWindow: (state: WindowState) => void;
  removeWindow: (key: string) => void;
  bringToFront: (key: string) => void;
  updateRect: (key: string, rect: { x: number; y: number; width: number; height: number }) => void;
  setMaximized: (key: string, maximized: boolean) => void;
  setMinimized: (key: string, minimized: boolean) => void;
  hydrate: (windows: WindowState[]) => void;
  clear: () => void;
}

export function makeWindowKey(agentId: string, panelType: PanelType): string {
  return `${agentId}:${panelType}`;
}

export const useWindowStore = create<WindowStoreState>((set) => ({
  openWindows: new Map(),
  windowStack: [],
  topZ: 1,

  upsertWindow: (state) =>
    set((s) => {
      const next = new Map(s.openWindows);
      next.set(state.key, state);
      const stack = s.windowStack.includes(state.key)
        ? s.windowStack
        : [...s.windowStack, state.key];
      const topZ = Math.max(s.topZ, state.zOrder);
      return { openWindows: next, windowStack: stack, topZ };
    }),

  removeWindow: (key) =>
    set((s) => {
      const next = new Map(s.openWindows);
      next.delete(key);
      return {
        openWindows: next,
        windowStack: s.windowStack.filter((k) => k !== key),
      };
    }),

  bringToFront: (key) =>
    set((s) => {
      const win = s.openWindows.get(key);
      if (!win) return s;
      const newZ = s.topZ + 1;
      const next = new Map(s.openWindows);
      next.set(key, { ...win, zOrder: newZ });
      const stack = [...s.windowStack.filter((k) => k !== key), key];
      return { openWindows: next, windowStack: stack, topZ: newZ };
    }),

  updateRect: (key, rect) =>
    set((s) => {
      const win = s.openWindows.get(key);
      if (!win) return s;
      const next = new Map(s.openWindows);
      next.set(key, { ...win, ...rect });
      return { openWindows: next };
    }),

  setMaximized: (key, maximized) =>
    set((s) => {
      const win = s.openWindows.get(key);
      if (!win) return s;
      const next = new Map(s.openWindows);
      // x/y/width/height NU se modifică — restore din maximized le folosește direct.
      next.set(key, { ...win, isMaximized: maximized, isMinimized: maximized ? false : win.isMinimized });
      return { openWindows: next };
    }),

  setMinimized: (key, minimized) =>
    set((s) => {
      const win = s.openWindows.get(key);
      if (!win) return s;
      const next = new Map(s.openWindows);
      next.set(key, { ...win, isMinimized: minimized });
      return { openWindows: next };
    }),

  hydrate: (windows) =>
    set(() => {
      const map = new Map<string, WindowState>();
      const stack: string[] = [];
      let maxZ = 1;
      const sorted = [...windows].sort((a, b) => a.zOrder - b.zOrder);
      for (const w of sorted) {
        map.set(w.key, w);
        stack.push(w.key);
        if (w.zOrder > maxZ) maxZ = w.zOrder;
      }
      return { openWindows: map, windowStack: stack, topZ: maxZ };
    }),

  clear: () =>
    set(() => ({ openWindows: new Map(), windowStack: [], topZ: 1 })),
}));
